import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const faqs = [
  {
    id: 1,
    question: "What is a Tender Red Review?",
    answer: "A Tender Red Review is an independent pre-submission check of your construction tender or estimate. We act as a 'red team' - an independent challenge to help you catch scope gaps, calculation errors, quote exclusions and commercial exposure before you submit your price."
  },
  {
    id: 2,
    question: "Does this replace our estimator?",
    answer: "No. The Tender Red Review does not replace your estimator or your internal pricing process. It acts as a final independent commercial safety gate - a second set of eyes to challenge the numbers before they leave your office. Your estimator's work forms the basis of the review."
  },
  {
    id: 3,
    question: "What documents do you need?",
    answer: "We need the tender documents, your current estimate or pricing spreadsheet, key supplier and subcontract quotations you're relying on, the tender deadline, and your programme or methodology if available. The more complete your review pack, the more thorough our findings."
  },
  {
    id: 4,
    question: "Is the review confidential?",
    answer: "Yes, completely. Your tender documents, estimate and quotations are commercially sensitive. We treat all submitted materials as strictly confidential. Nothing is shared, submitted, amended or communicated to the principal, client, supplier or subcontractor without your explicit instruction."
  },
  {
    id: 5,
    question: "Is 24-hour turnaround guaranteed?",
    answer: "24 hours is our target turnaround for substantially complete standard reviews. However, actual timing depends on tender complexity, review pack completeness and current workload. We confirm scope, fee and turnaround before commencing any review - no surprises."
  },
  {
    id: 6,
    question: "What size tender suits the founding offer?",
    answer: "The founding offer is best suited to substantially complete small-to-mid-sized construction tenders in New Zealand or Australia. Larger, incomplete or unusually complex packages are quoted separately before work begins. We'll confirm suitability before accepting the engagement."
  },
  {
    id: 7,
    question: "Do you remeasure the whole tender?",
    answer: "No. The standard review is not a complete independent remeasurement or a replacement estimate. We check scope coverage, estimate integrity, calculation logic, quote gaps and programme consistency - but we work from your numbers and documents, not from scratch."
  },
  {
    id: 8,
    question: "What does GO / GO WITH ACTIONS / HOLD mean?",
    answer: "These are our release recommendations. GO means we found no material commercial blockers. GO WITH ACTIONS means the tender is broadly sound but has identified items you should address or qualify before submission. HOLD means we found material issues that warrant resolution before the price leaves your office."
  },
  {
    id: 9,
    question: "Can you review New Zealand and Australian tenders?",
    answer: "Yes. We actively serve contractors in both New Zealand and Australia. We understand the construction language, procurement methods and commercial practices in both markets. Pricing is NZ$595 for New Zealand customers and A$545 for Australian customers."
  },
  {
    id: 10,
    question: "What happens if our tender is incomplete?",
    answer: "We'll assess the review pack when you submit it. If key documents are missing or the estimate is too preliminary, we'll let you know what's needed before confirming scope and fee. Incomplete tenders may be quoted separately or deferred until substantially complete."
  }
];

export default function TenderFAQ() {
  const [openId, setOpenId] = useState(null);

  const toggleFaq = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section
      id="tender-faq"
      className="py-16 md:py-24 border-t-4 border-black bg-zinc-50"
      data-testid="tender-faq-section"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="mb-12 md:mb-16 text-center">
          <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
            Questions
          </span>
          <h2
            className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mt-4"
            data-testid="tender-faq-title"
          >
            Frequently Asked
          </h2>
        </div>

        {/* FAQ items */}
        <div className="space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="border-2 border-black bg-white"
              data-testid={`faq-${faq.id}`}
            >
              <button
                onClick={() => toggleFaq(faq.id)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-zinc-50 transition-colors"
                aria-expanded={openId === faq.id}
              >
                <span className="font-heading font-bold text-lg uppercase pr-4">
                  {faq.question}
                </span>
                {openId === faq.id ? (
                  <ChevronUp size={24} className="flex-shrink-0" />
                ) : (
                  <ChevronDown size={24} className="flex-shrink-0" />
                )}
              </button>
              {openId === faq.id && (
                <div className="px-6 pb-6 border-t border-zinc-200">
                  <p className="font-body text-zinc-700 leading-relaxed pt-4">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
